/**
 * Extracts existing files from fund mappings
 * @param {Array} fundMappings - Array of fund mapping objects
 * @returns {Array} Array of existing file objects with metadata
 */
export const extractExistingFiles = (fundMappings = []) => {
  const existingFiles = [];

  fundMappings.forEach(mapping => {
    // Extract bulk files
    if (mapping.bulkFiles && Array.isArray(mapping.bulkFiles)) {
      mapping.bulkFiles.forEach(fileMapping => {
        if (fileMapping.currentFile) {
          existingFiles.push({
            name: fileMapping.currentFile.name,
            size: fileMapping.currentFile.size,
            type: fileMapping.currentFile.file_type,
            id: fileMapping.currentFile.id,
            source: 'bulk'
          });
        }
      });
    }

    // Extract per-fund files
    if (mapping.perFundFiles && Array.isArray(mapping.perFundFiles)) {
      mapping.perFundFiles.forEach(fileMapping => {
        if (fileMapping.currentFile) {
          existingFiles.push({
            name: fileMapping.currentFile.name,
            size: fileMapping.currentFile.size,
            type: fileMapping.currentFile.file_type,
            id: fileMapping.currentFile.id,
            source: 'perFund'
          });
        }
      });
    }
  });

  return existingFiles;
};

/**
 * Detects duplicate files between existing files and new files.
 * Comparison is done by file name and size to identify duplicates.
 * @param {FileList|Array} newFiles - Files being uploaded
 * @param {Array} existingFiles - Existing files in the system
 * @returns {Array} Array of duplicate file objects with details
 */
export const detectDuplicateFiles = (newFiles, existingFiles = []) => {
  const duplicates = [];
  const newFilesArray = Array.isArray(newFiles) ? newFiles : Array.from(newFiles);

  newFilesArray.forEach(newFile => {
    const duplicate = existingFiles.find(
      existingFile =>
        existingFile.name.toLowerCase() === newFile.name.toLowerCase() &&
        existingFile.size === newFile.size
    );

    if (duplicate) {
      duplicates.push({
        fileName: newFile.name,
        fileSize: newFile.size,
        existingFile: duplicate,
        // Store the actual File object so it can be re-uploaded after user confirmation
        newFile
      });
    }
  });

  return duplicates;
};

/**
 * Filters out duplicate files from the upload
 * @param {FileList|Array} newFiles - Files being uploaded
 * @param {Array} duplicates - Array of duplicate file objects
 * @returns {Array} Array of non-duplicate files
 */
export const filterOutDuplicates = (newFiles, duplicates = []) => {
  const newFilesArray = Array.isArray(newFiles) ? newFiles : Array.from(newFiles);
  const duplicateKeys = new Set(duplicates.map(d => `${d.fileName}::${d.fileSize}`));

  return newFilesArray.filter(file => !duplicateKeys.has(`${file.name}::${file.size}`));
};
